import {
  PeopleEmailStatus,
  PersonEmailDomainType,
} from '@aibees/elephant/feature/search-table-people/types';
import type { SxStyleThemeValueFunc } from '@aibees/shared/theme';
import { capitalize } from '@aibees/shared/utils';

export const STATUS_COLOR: Record<
  PeopleEmailStatus,
  string | SxStyleThemeValueFunc
> = {
  VALID: (theme) => theme.palette.success.main,
  CATCH_ALL: (theme) => theme.palette.warning[500],
  LOCKED: (theme) => theme.palette.info.main,
  INVALID: (theme) => theme.palette.error[500],
  ERROR: (theme) => theme.palette.grey[500],
  NOT_FOUND: (theme) => theme.palette.grey[500],
  UNKNOWN: (theme) => theme.palette.grey[500],
  DOMAIN_NOT_FOUND: (theme) => theme.palette.grey[500],
  DOMAIN_NOT_EXIST: (theme) => theme.palette.grey[500],
  DOMAIN_NOT_ACCEPT: (theme) => theme.palette.grey[500],
  DO_NOT_MAIL: (theme) => theme.palette.grey[500],
  UPDATE: (theme) => theme.palette.primary[600],
  EXPIRED: (theme) => theme.palette.primary[600],
  // Warning, not primary: an outdated email is a caution about the value shown,
  // not a call to action like UPDATE.
  OUTDATED: (theme) => theme.palette.warning[500],
};

export function emailStatusFormatter({
  unlocked,
  needsEnrichEmail,
  hasUpdate,
  status,
}: {
  unlocked: boolean;
  needsEnrichEmail: boolean;
  hasUpdate: boolean;
  status?: PeopleEmailStatus;
}) {
  const humanReadableStatus = capitalize(
    status === PeopleEmailStatus.VALID
      ? 'Verified'
      : status?.replaceAll('_', ' ') ?? ''
  );
  return !unlocked
    ? 'Unknown'
    : // A company change makes a previously valid email outdated — it belongs to
    // the previous company. Not the same thing as Invalid, which is the verifier
    // saying the address itself is bad.
    needsEnrichEmail
    ? capitalize(PeopleEmailStatus.OUTDATED)
    : hasUpdate
    ? 'Update'
    : status === PeopleEmailStatus.ERROR
    ? 'Not-Found'
    : humanReadableStatus;
}

export function humanReadableEmailType(type?: PersonEmailDomainType) {
  switch (type) {
    case PersonEmailDomainType.SMTP:
      return 'SMTP';
    case PersonEmailDomainType.CATCH_ALL:
      return 'Catch-All';
    case PersonEmailDomainType.UNKNOWN:
      return 'Unknown';
    default:
      return 'Unknown';
  }
}

export type ChipColorParams = {
  status?: PeopleEmailStatus;
  unlocked: boolean;
  needsEnrichEmail: boolean;
  hasUpdate: boolean;
  reverify: boolean;
};

export function getChipColor({
  status,
  unlocked,
  needsEnrichEmail,
  hasUpdate,
  reverify,
}: ChipColorParams) {
  const finalStatus = !unlocked
    ? PeopleEmailStatus.UNKNOWN
    : needsEnrichEmail
    ? PeopleEmailStatus.OUTDATED
    : hasUpdate
    ? PeopleEmailStatus.UPDATE
    : reverify
    ? PeopleEmailStatus.CATCH_ALL
    : status ?? PeopleEmailStatus.LOCKED;

  switch (finalStatus) {
    case PeopleEmailStatus.VALID:
      return 'default';
    case PeopleEmailStatus.CATCH_ALL:
      return 'warning';
    case PeopleEmailStatus.LOCKED:
      return 'info';
    case PeopleEmailStatus.INVALID:
      return 'error';
    case PeopleEmailStatus.ERROR:
    case PeopleEmailStatus.NOT_FOUND:
    case PeopleEmailStatus.UNKNOWN:
    case PeopleEmailStatus.DOMAIN_NOT_FOUND:
    case PeopleEmailStatus.DOMAIN_NOT_EXIST:
    case PeopleEmailStatus.DOMAIN_NOT_ACCEPT:
    case PeopleEmailStatus.DO_NOT_MAIL:
      return 'default';
    case PeopleEmailStatus.OUTDATED:
      return 'warning';
    case PeopleEmailStatus.UPDATE:
    case PeopleEmailStatus.EXPIRED:
      return 'primary';
    default:
      return 'default';
  }
}

export function getDomainTypeChipColor(params: ChipColorParams) {
  return getChipColor(params);
}

export function getStatusChipColor({
  status,
  unlocked,
  needsEnrichEmail,
  hasUpdate,
  reverify,
}: ChipColorParams) {
  const finalStatus = !unlocked
    ? PeopleEmailStatus.UNKNOWN
    : needsEnrichEmail
    ? PeopleEmailStatus.OUTDATED
    : hasUpdate
    ? PeopleEmailStatus.UPDATE
    : reverify
    ? PeopleEmailStatus.CATCH_ALL
    : status ?? PeopleEmailStatus.LOCKED;

  switch (finalStatus) {
    case PeopleEmailStatus.VALID:
      return 'success';
    case PeopleEmailStatus.CATCH_ALL:
      return 'warning';
    case PeopleEmailStatus.LOCKED:
      return 'info';
    case PeopleEmailStatus.INVALID:
      return 'error';
    case PeopleEmailStatus.ERROR:
    case PeopleEmailStatus.NOT_FOUND:
    case PeopleEmailStatus.UNKNOWN:
    case PeopleEmailStatus.DOMAIN_NOT_FOUND:
    case PeopleEmailStatus.DOMAIN_NOT_EXIST:
    case PeopleEmailStatus.DOMAIN_NOT_ACCEPT:
    case PeopleEmailStatus.DO_NOT_MAIL:
      return 'default';
    case PeopleEmailStatus.OUTDATED:
      return 'warning';
    case PeopleEmailStatus.UPDATE:
    case PeopleEmailStatus.EXPIRED:
      return 'primary';
    default:
      return 'default';
  }
}
